import { api } from './http'
import { and, searchParam } from './filters'

// Cobro y proceso de cobro (caja) del flujo ágil.
// Payloads VALIDADOS con POST reales (2026-06-25): proceso cobro id 129, cobro id 1121
// (estado_cobro_emitido). Objetos anidados van trimeados a {id}; estado del proceso de cobro
// y locacion/sucursal/unidadNegocio van como id PELADO (número), igual que el controller Angular.

const idOf = (o) => (o && o.id != null ? { id: o.id } : null)
const pad = (n) => String(n).padStart(2, '0')

// Fecha en el formato que espera Qualita.
export function fechaYMD(d = new Date()) {
  return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())}` // yyyy/MM/dd
}
export function fechaDMY(d = new Date()) {
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}` // DD/MM/YYYY (para filtros like)
}

// --- Proceso de cobro abierto de la caja/usuario para hoy (form.js:8661-8682) ---
export function buscarProcesoCobroAbierto({ cajaId, usuarioId, fecha = fechaDMY() }) {
  const filtro = and([
    { path: 'caja.id', equals: cajaId },
    { path: 'usuario.id', equals: usuarioId },
    { path: 'estado.codigo', notEquals: 'estado_procesocobro_cerrado' },
    { path: 'fechaHoraInicio', like: fecha },
  ])
  return api.get('/procesocobro', { params: searchParam(filtro) }).then((r) => {
    const list = Array.isArray(r.data) ? r.data : r.data?.list || []
    return list[0] || null
  })
}

// --- Abrir (apertura) proceso de cobro (sendNewProcesoCobro, form.js:9027) ---
// `estado` va como id PELADO; caja/usuario como {id}; locacion como id pelado.
export function abrirProcesoCobro({ estadoAbiertoId, caja, usuario, locacionId, montoBaseEfectivo = 0 }) {
  const payload = {
    estado: estadoAbiertoId,
    caja: idOf(caja),
    fechaHoraInicio: fechaYMD(),
    usuario: idOf(usuario),
    locacion: locacionId,
    montoBaseEfectivo: Number(montoBaseEfectivo) || 0,
    recaudacionDepositar: 0,
    chequesDiferidosVencidos: 0,
    totalDepositar: 0,
  }
  return api.post('/procesocobro', payload).then((r) => r.data)
}

// --- Guardar cobro (POST /cobros?facturacionagil=true&mantenimientocobro=false) ---
// CobroDetalle.moneda es @NotNull => cada detalle lleva moneda. cobrosComprobantes paga el comprobante.
export function guardarCobro({
  caja, cliente, moneda, procesoCobro, tipoCobro, tipoCobroEntidad, usuario, locacion,
  comprobante, formaPago, importe,
}) {
  const monto = Number(importe) || 0
  const total = Number(comprobante?.montoTotal) || monto
  const saldo = Number(comprobante?.saldo ?? comprobante?.montoTotal) || monto
  const payload = {
    caja: idOf(caja),
    cliente: idOf(cliente),
    moneda: idOf(moneda),
    procesoCobro: idOf(procesoCobro),
    tipoCobro: idOf(tipoCobro),
    tipoCobroEntidad: idOf(tipoCobroEntidad),
    usuario: idOf(usuario),
    fecha: fechaYMD(),
    importeAnticipo: 0,
    cobroDetalle: [{ formaPago: idOf(formaPago), moneda: idOf(moneda), importe: monto, importeVueltoItem: 0 }],
    cobrosComprobantes: [{
      comprobanteVenta: idOf(comprobante),
      importePagar: total,
      saldoConvertido: saldo,
      tipoCambio: Number(comprobante?.cambio) || 1,
    }],
    locacion: locacion?.id ?? null,
    sucursal: locacion?.sucursal?.id ?? null,
    unidadNegocio: locacion?.unidadNegocio?.id ?? null,
  }
  return api
    .post('/cobros', payload, { params: { facturacionagil: true, mantenimientocobro: false } })
    .then((r) => r.data)
}

export function getProcesoCobro(id, view) {
  return api.get(`/procesocobro/${id}`, { params: view ? { view } : {} }).then((r) => r.data)
}

// --- Cierre de caja (cierre del proceso de cobro) ---
// FUENTE: ProcesoCobroFactory.cerrar -> PUT /procesocobro/cerrar/{id}?user={accesoId}.
// VALIDADO EN VIVO (2026-06-25, dpyagil/MT): se abrió el proceso de prueba 130 y se cerró con
//   PUT /procesocobro/cerrar/130?user=1  -> HTTP 200, estado -> estado_procesocobro_cerrado.
// `user` = id del AccesoSistema (acceso por defecto) del usuario logueado; en el MT = 1.
// El cierre totaliza los cobros del proceso y graba recaudacionDepositarCierre (arrastre al
// apertura del día siguiente). NO confundir con el id del Usuario (también 1 en el MT, pero
// el contrato es el id del AccesoSistema, ProcesoCobroFactory.cerrar(id, idAcceso)).
export function cerrarProcesoCobro(id, user) {
  return api.put(`/procesocobro/cerrar/${id}`, null, { params: { user } }).then((r) => r.data)
}
// VALIDADO EN VIVO (2026-06-25): PUT /procesocobro/reabrir/130 -> HTTP 200 (revierte el cierre).
export function reabrirProcesoCobro(id) {
  return api.put(`/procesocobro/reabrir/${id}`).then((r) => r.data)
}
// NO DISPONIBLE en este backend: GET /procesocobro/diferencias/{id} -> HTTP 404
// ("RESTEASY003210: Could not find resource"). El endpoint existe en el factory Angular pero el
// WAR del MT no lo expone. Se deja la función por compatibilidad, pero NO usarla en la UI.
export function getDiferenciasProcesoCobro(id) {
  return api.get(`/procesocobro/diferencias/${id}`).then((r) => r.data)
}

// --- Arqueo de caja (POST /arqueocajaventa -> ArqueoCajaVentaService.persist -> persist2) ---
// VALIDADO EN VIVO (2026-06-25, dpyagil/MT): POST mínimo -> HTTP 200, arqueo id=1 creado contra
// el proceso 129 (quedó ABIERTO, solo se setea arqueado='S', NO se cierra).
//
// El backend (ArqueoCajaVentaBusinessImpl.persist2) hace casi todo del lado servidor:
//   - REQUIERE procesoCobro.{id} (lo dereferencia: procesoCobro.getUsuario().getUsername()).
//   - SOBREESCRIBE descripcion ("Arqueo Caja Numero {id}"), vrTipoArqueo ("Arqueo definitivo"),
//     idEmpresa=1, idUnidad=1, y CALCULA totalConceptos/totalValores/diferencia + detalleArqueos
//     y declaracionEfecs recorriendo los cobros del proceso y los catálogos (ConceptoArqueo,
//     AsignacionMoneda/DenominacionMoneda). El front NO arma esos detalles.
//   - Los detalleArqueos/declaracionEfecs salen vacíos si el tenant no tiene esos catálogos
//     cargados (caso del MT hoy). Eso es config de datos del tenant, no del payload.
//
// Campos @NotNull que el persist genérico valida y por eso DEBEN ir en el JSON (aunque algunos
// luego se sobreescriban): fecha, vrTipoArqueo, usrCre, fecCre, idEmpresa, idUnidad.
// Shape validado:
//   { procesoCobro:{id}, caja:{id}, fecha:'yyyy/MM/dd', vrTipoArqueo, usrCre, fecCre:'yyyy/MM/dd',
//     idEmpresa:1, idUnidad:1 }
export function registrarArqueo(payload) {
  return api.post('/arqueocajaventa', payload).then((r) => r.data)
}
